import type { ReactNode } from "react"
import { ExternalLink } from "lucide-react"
import { observer } from "mobx-react-lite"
import { Link } from "react-router-dom"

import type { ManualClPoolStore } from "@/domains/thick-v2/manual-cl-store"
import { Button } from "@/components/button"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/tooltip"
import { BodyXs } from "@/domains/design/components/body-xs"
import { cn } from "@/domains/library"
import { CL_PRODUCT_COPY } from "@/domains/manual-cl-page/cl-product-copy"
import { AprBadge } from "@/domains/thick-v2/components/apr-badge"
import { RangeAprTooltipBody } from "@/domains/thick-v2/components/range-apr-tooltip-body"
import { DisplayTokens } from "@/domains/tokens/components/display-tokens"

/**
 * Pool detail header — pair icons, pool name, fee tier, quick stats, and a
 * prominent "Add liquidity" CTA.
 *
 * Reads directly from `ManualClPoolStore` (token stores, range-APR computed,
 * display strings), so the outer shell is wrapped in `observer` to stay in
 * sync as the pool's observables mutate. Visual language mirrors the Manual CL
 * list page: `bg-p300` outer shell, `text-t100`/`BodyXs` typography,
 * `rounded-lg` corners.
 */
export const PoolDetailHeaderView = observer(function PoolDetailHeaderView({ pool }: { pool: ManualClPoolStore }) {
  const rangeAprResult = pool.rangeApr7d
  const fullRangeApr = pool.displayApr
  return (
    <TooltipProvider>
      <div className="flex flex-col gap-4 rounded-lg border border-p300-light bg-p300 p-4 sm:flex-row sm:items-center sm:justify-between lg:p-6">
        <div className="flex items-center gap-3">
          <DisplayTokens tokens={[pool.token0, pool.token1]} tokenSize={30} />
          <div className="flex flex-col">
            <h1 className="text-lg font-medium text-t100">
              {pool.token0.symbol} / {pool.token1.symbol}
            </h1>
            <BodyXs className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-t200">
              <span>
                {CL_PRODUCT_COPY.v2.subtitle} · Spacing {pool.tickSpacing} · Fee {pool.displayFee}
              </span>
              <span aria-hidden>·</span>
              <a
                href={`https://basescan.org/address/${pool.address.raw}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-primary-400 transition-colors hover:text-primary-300 hover:underline"
                aria-label={`View ${pool.token0.symbol} / ${pool.token1.symbol} pool on Basescan`}
                title="View pool contract on Basescan"
              >
                View on Basescan
                <ExternalLink className="h-3 w-3" aria-hidden />
              </a>
            </BodyXs>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <HeaderStat label="Current price" value={pool.displayMidPrice} />
          <HeaderStat
            label="7D range APR"
            value={
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex cursor-help">
                    <AprBadge value={rangeAprResult.formattedPct} variant="pool" />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-xs">
                  <RangeAprTooltipBody rangeAprResult={rangeAprResult} fullRangeApr={fullRangeApr} />
                </TooltipContent>
              </Tooltip>
            }
          />
          <Button variant="primary" asChild size="md">
            <Link to={`/liquidity/cl/v2/${pool.address.raw}/create-position`}>Add new position</Link>
          </Button>
        </div>
      </div>
    </TooltipProvider>
  )
})

function HeaderStat({ label, value }: { label: string; value: ReactNode }) {
  const content =
    typeof value === "string" || typeof value === "number" ? (
      <span className="text-sm font-medium text-t100">{value || "—"}</span>
    ) : (
      value
    )
  return (
    <div className={cn("flex flex-col gap-0.5")}>
      <BodyXs className="uppercase tracking-wide text-t300">{label}</BodyXs>
      {content}
    </div>
  )
}
